<template>
  <div v-show="loadingCompleted">
      <h5 class="m-4 font-weight-normal">{{ $t('point-history.gift-point') }}</h5>
      <div class="bg-gray100 p-4 d-flex flex-column align-items-center">
        <h6 class="font-size-md">{{ $t('point-history.please-enter-recipient-info') }}</h6>
        <div class="my-3 w-100">
          <Mobile
            :required="true"
            :editable="true"
            :titleHidden="true"
            :answer="mobileData"
            @updateData="mobileData = $event.value"
            @validateDone="setMobileValidateState"
            ref="mobile">
            <template v-slot:bottom>
              <div class="w-100 invalid-feedback" :class="{'d-block': showCustomMobileError}">
                {{ $t('point-history.member-not-found') + '，' }}<a href="javascript: void(0)" class="invalid-feedback d-inline" @click="inviteFriend"><u>{{ $t('point-history.invite-friend') }}</u></a>
              </div>
            </template>
          </Mobile>
          <!-- 驗證碼 begin -->
          <VerificationCodeBlock ref="verificationCode"/>
          <!-- 驗證碼 end -->
        </div>
        <h6 class="font-size-md">{{ $t('point-history.please-enter-transfering-amount') }}</h6>
        <div class="my-3 w-100">
          <!-- 輸入轉贈點數 begin -->
          <b-form-group>
            <div class="d-flex">
              <b-input
                type="number"
                class="bg-white mr-n1 text-right font-weight-bold text-customize"
                min="0"
                lazy-formatter
                v-model="transferAmount"
                :formatter="formatNumericString"
                :state="transferAmountValidateState">
              </b-input>
              <b-button class="text-nowrap" @click="setMaximum">{{ $t('point-history.use-all') }}</b-button>
            </div>
            <div class="mt-1 d-flex justify-content-between font-size-md">
              <div class="text-left text-danger flex-grow-1">{{ pointWarning }}</div>
              <div class="text-right">
                <div v-if="maximumTransferAmount">{{ `${$t('point-history.maximum-transfer-amount')} ` }}{{ maximumTransferAmount | currency }}</div>
                <div>{{ `${$t('point-history.usable-amount')} ` }}{{ availableAmount | currency }}</div>
              </div>
            </div>
          </b-form-group>
          <!-- 輸入轉贈點數 end -->
        </div>
        <button
          type="button"
          class="btn px-3 py-2 w-100 border-0 bg-customize btn-primary"
          @click="submit">
          {{ $t('global.send') }}
        </button>
        <article class="my-2 font-size-md text-gray600" v-html="instruction"></article>
      </div>
  </div>
</template>

<script>
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import Mobile from '@/components/models/Mobile.vue';
import VerificationCodeBlock from '@/components/verificationCode/VerificationCodeBlock.vue';
import dateTimeFormatter from '@/utils/dateTimeFormatter';
import pageSettingGetter from '@/utils/pageSettingGetter';
import pmtService from '@/services/pmtService';

export default {
  components: {
    Mobile,
    VerificationCodeBlock,
  },
  data() {
    return {
      // 資料是否完成載入
      loadingCompleted: false,
      // 受贈人手機
      mobileData: {
        callingCode: '',
        mobile: '',
      },
      // 手機號碼格式是否正確
      mobileValid: false,
      // 顯示特殊手機錯誤訊息
      showCustomMobileError: false,
      // 轉贈點數
      transferAmount: 0,
      // 轉贈點數驗證狀態
      transferAmountValidateState: undefined,
      // 單筆最大轉贈點數
      maximumTransferAmount: null,
      // 可用點數
      availableAmount: 0,
      // 轉贈點數錯誤提示
      pointWarning: '',
      // 說明文字
      instruction: '',
      // 品牌名稱
      brandName: '',
      // 點數單位
      pointUnit: '',
      // 會員名稱
      memberName: '',
      // 會員手機號碼
      memberMobile: '',
    };
  },
  computed: {
    // 完整手機號碼
    fullMobile() {
      const vm = this;
      return `${vm.mobileData.callingCode}${vm.mobileData.mobile}`;
    },
  },
  created() {
    const vm = this;

    // 取得頁面設定資料
    const getPageSetting = vm.$store.dispatch('getPageSetting', 'UserPointHistory');
    getPageSetting.then((setting) => {
      vm.maximumTransferAmount = setting.transferPointUpperBound;
      vm.pointUnit = pageSettingGetter.getFunctionWording(setting, 'UserPointUnit');
    });

    // 取得會員積點
    const getMemberBonus = vm.$store.dispatch('getMemberBonus');
    getMemberBonus.then((memberBonus) => {
      const now = new Date();
      const dateLimit = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, now.getUTCHours(), now.getUTCMinutes()));
      vm.availableAmount = memberBonus.summary
        .filter((summary) => dateTimeFormatter.parseTaiwanDateTime(summary.pointExpiry) > dateLimit)
        .reduce((total, summary) => total + (summary.point - summary.pointUsed), 0);
    });

    // 取得公司資訊
    const getCompanyPermission = vm.$store.dispatch('getCompanyPermission');
    getCompanyPermission.then((companyPermission) => {
      vm.brandName = companyPermission.companyName;
    });

    // 取得會員資料
    const getMemberData = vm.$store.dispatch('getMemberData');
    getMemberData.then((memberData) => {
      vm.memberName = memberData.name;
      vm.memberMobile = `${memberData.callingCode}${memberData.mobile}`;
    });

    Promise.all([getPageSetting, getMemberBonus, getCompanyPermission, getMemberData])
      .then((res) => {
        vm.instruction = pageSettingGetter.getFunctionWording(res[0], 'TransferPointNotice').replace(/{BrandName}/g, res[2].companyName);
        vm.loadingCompleted = true;
      });
  },
  inject: ['openPopUp'],
  methods: {
    // 設定手機號碼驗證狀態
    setMobileValidateState() {
      const vm = this;
      vm.showCustomMobileError = false;

      vm.$nextTick(() => {
        // 格式驗證失敗
        if (!vm.$refs.mobile.validateState) {
          vm.mobileValid = false;
        // 輸入的手機號碼為自己的手機號碼
        } else if (vm.fullMobile === vm.memberMobile) {
          vm.$refs.mobile.setErrorMessageFromOutside({
            errorMessage: vm.$t('point-history.cannot-transfer-to-yourself'),
            validateState: false,
          });
          vm.mobileValid = false;
        // 驗證成功
        } else {
          vm.$refs.mobile.setErrorMessageFromOutside({
            errorMessage: '',
            validateState: undefined,
          });
          vm.mobileValid = true;
        }
      });
    },
    // 邀請好友
    inviteFriend() {
      const vm = this;

      // 組成邀請好友文案
      const text = vm.$t('point-history.invite-friend-content', { brandName: vm.brandName, pointUnit: vm.pointUnit });
      const indexRoute = vm.$router.resolve({ name: 'index', query: { companyid: vm.$store.state.companyId, membersourceid: vm.$store.state.memberSourceId } });
      const content = `${text} ${window.location.origin}${indexRoute.href}`;

      // // 若支援分享功能，分享邀請文字
      // if (navigator.share) {
      //   navigator.share({
      //     text: content,
      //   });
      // } else

      // 若支援剪貼簿功能，複製邀請文字
      if (navigator.clipboard) {
        navigator.clipboard.writeText(content)
          .then(() => {
            // 顯示複製成功pop-up
            vm.openPopUp({
              isOpen: true,
              mode: 'hint',
              title: vm.$t('point-history.content-copied'),
              content: vm.$t('point-history.share-with-friend-immediately'),
            });
          });
      }

      if (document.execCommand) {
        vm.openPopUp({
          isOpen: true,
          mode: 'hint',
          title: vm.$t('point-history.content-copied'),
          content: vm.$t('point-history.share-with-friend-immediately'),
        });

        const oInput = document.createElement('textarea'); // 建立一個隱藏input（重要！）；因 input不支援換行，改用 textarea
        oInput.value = content; // 賦值
        document.body.appendChild(oInput);
        oInput.select(); // 選擇物件
        document.execCommand('copy'); // 執行瀏覽器複製命令
        oInput.className = 'oInput';
        oInput.style.display = 'none';
      }
    },
    // 格式化數字字串
    formatNumericString(value) {
      value = parseInt(value, 10);
      if (Number.isNaN(value)) value = 0;
      if (value < 0) value *= -1;
      return value.toString();
    },
    // 設定最大轉贈點數
    setMaximum() {
      const vm = this;
      vm.transferAmount = (vm.maximumTransferAmount && vm.maximumTransferAmount < vm.availableAmount) ? vm.maximumTransferAmount.toString() : vm.availableAmount.toString();
    },
    // 送出
    submit() {
      const vm = this;
      let valid = true;

      // 驗證點數
      const transferAmountValid = vm.validateTransferAmount();
      if (!transferAmountValid) {
        valid = false;
      }

      // 確認驗證碼
      const verificationCodeValid = vm.$refs.verificationCode.verify();
      if (!verificationCodeValid) {
        valid = false;
      }

      // 驗證手機號碼，若有問題，不需繼續檢查
      vm.$refs.mobile.validate();
      if (!vm.mobileValid) {
        return;
      }

      // 檢查手機號碼是否存在於會員資料中
      vm.$store.dispatch('checkMemberExistence', { mobile: vm.fullMobile })
        .then((res) => {
          // 檢查失敗，顯示錯誤訊息
          if (res.response.status !== '200') {
            vm.$refs.mobile.setErrorMessageFromOutside({
              errorMessage: '',
              validateState: false,
            });
            vm.showCustomMobileError = true;
          // 即使手機號碼存在，其他驗證未通過時不顯示pop-up
          } else {
            if (!valid) {
              return;
            }

            // 顯示確認pop-up
            vm.openPopUp({
              isOpen: true,
              mode: 'default',
              title: vm.$t('point-history.confirm-transfer'),
              content: vm.$t('point-history.confirm-transfer-message', { amount: vm.transferAmount, mobile: vm.fullMobile }),
              confirm() {
                vm.transferBonus();
              },
            });
          }
        });
    },
    // 驗證點數欄位
    validateTransferAmount() {
      const vm = this;
      const amount = parseInt(vm.transferAmount, 10);

      // 檢查點數是否足夠
      if (amount > vm.availableAmount) {
        vm.pointWarning = vm.$t('point-history.insufficient-amount');
        vm.transferAmountValidateState = false;
      // 檢查點數是否超過單筆轉贈上限或是為0
      } else if (amount === 0 || (vm.maximumTransferAmount && amount > vm.maximumTransferAmount)) {
        vm.pointWarning = vm.$t('point-history.please-confirm-transfer-amount');
        vm.transferAmountValidateState = false;
      // 檢查通過
      } else {
        vm.pointWarning = '';
        vm.transferAmountValidateState = undefined;
      }

      return vm.transferAmountValidateState === undefined;
    },
    // 轉贈點數
    transferBonus() {
      const vm = this;
      const body = {
        companyId: vm.$store.state.companyId,
        memberSourceId: vm.$store.state.memberSourceId,
        memberId: vm.$store.state.memberId,
        transferMobile: vm.fullMobile,
        points: parseInt(vm.transferAmount, 10),
        noticeTitle: vm.$t('point-history.transfer-notice-title', { pointUnit: vm.pointUnit }),
        noticeContent: vm.$t('point-history.transfer-notice-content', { memberName: vm.memberName, amount: vm.transferAmount, pointUnit: vm.pointUnit }),
      };

      // 呼叫API
      vm.$loading(
        pmtService.postMemberBonusTransfer(body).pipe(
          catchError((err) => of({ response: err.data, status: err.status })),
          tap((res) => {
            let params = {};
            // 依API回應決定完成頁的內容
            switch (res.response.status) {
            // 轉贈成功
              case '200':
                params = {
                  title: vm.$t('point-history.transfer-complete'),
                  description: vm.$t('point-history.already-transfer-to', { mobile: vm.fullMobile }),
                  iconType: 'success',
                  primaryButton: {
                    text: vm.$t('point-history.inform-friend'),
                    action() { vm.informFriend(); },
                  },
                };
                break;
                // 點數不足
              case '747':
                params = {
                  title: vm.$t('point-history.transfer-error'),
                  description: vm.$t('point-history.insufficient-amount'),
                  iconType: 'failed',
                  primaryButton: {
                    text: vm.$t('global.understand'),
                    action() {
                      vm.$router.push({
                        name: 'pointHistory',
                        query: {
                          companyid: vm.$store.state.companyId,
                          membersourceid: vm.$store.state.memberSourceId,
                        },
                      });
                    },
                  },
                };
                break;
                // 點數為0
                // 點數超過上限
              case '748':
              case '749':
                params = {
                  title: vm.$t('point-history.transfer-error'),
                  description: vm.$t('point-history.please-confirm-transfer-amount'),
                  iconType: 'failed',
                  primaryButton: {
                    text: vm.$t('global.understand'),
                    action() {
                      vm.$router.push({
                        name: 'pointHistory',
                        query: {
                          companyid: vm.$store.state.companyId,
                          membersourceid: vm.$store.state.memberSourceId,
                        },
                      });
                    },
                  },
                };
                break;
                // 其他錯誤
              default:
                params = {
                  title: vm.$t('point-history.transfer-error'),
                  description: vm.$t('point-history.try-again-later'),
                  iconType: 'failed',
                  primaryButton: {
                    text: vm.$t('global.understand'),
                    action() {
                      vm.$router.push({
                        name: 'pointHistory',
                        query: {
                          companyid: vm.$store.state.companyId,
                          membersourceid: vm.$store.state.memberSourceId,
                        },
                      });
                    },
                  },
                };
                break;
            }

            // 前往完成頁
            params.pageTitle = document.title;
            vm.$router.push({
              name: 'complete',
              query: {
                companyid: vm.$store.state.companyId,
                membersourceid: vm.$store.state.memberSourceId,
              },
              params,
            });
          }),
        ),
      );
    },
    // 通知好友
    informFriend() {
      const vm = this;

      // 組成通知好友文案
      const text = vm.$t('point-history.inform-friend-content', { brandName: vm.brandName, pointUnit: vm.pointUnit });
      const indexRoute = vm.$router.resolve({ name: 'notification', query: { companyid: vm.$store.state.companyId, membersourceid: vm.$store.state.memberSourceId } });
      const content = `${text} ${window.location.origin}${indexRoute.href}`;

      // 若支援分享功能，分享通知文字
      // if (navigator.share) {
      //   navigator.share({
      //     text: content,
      //   });
      // } else

      // 若支援剪貼簿功能，複製通知文字
      // if (navigator.clipboard) {
      //   navigator.clipboard.writeText(content)
      //     .then(() => {
      //     });
      // }

      const u = navigator.userAgent;
      const isiOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);

      if (document.execCommand) {
        if (isiOS) {
          const oInput = document.createElement('textarea'); // 建立一個隱藏input（重要！）；因 input不支援換行，改用 textarea
          oInput.disabled = true;
          oInput.value = content; // 賦值
          document.body.appendChild(oInput);
          oInput.select(); // 選擇物件
          document.execCommand('copy'); // 執行瀏覽器複製命令
          oInput.blur();
          oInput.className = 'oInput';
          oInput.style.display = 'none';
        } else {
          const oInput = document.createElement('textarea'); // 建立一個隱藏input（重要！）；因 input不支援換行，改用 textarea
          oInput.value = content; // 賦值
          document.body.appendChild(oInput);
          oInput.select(); // 選擇物件
          document.execCommand('copy'); // 執行瀏覽器複製命令
          oInput.className = 'oInput';
          oInput.style.display = 'none';
        }
      }

      // 顯示複製成功pop-up
      vm.openPopUp({
        isOpen: true,
        mode: 'hint',
        title: vm.$t('point-history.content-copied'),
        content: vm.$t('point-history.share-with-friend-immediately'),
      });
    },
  },
};
</script>
